import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Container, Typography, CssBaseline } from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import { authCardShellSx, authPageOuterSx } from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import newEraLogo from "../NewEraLogo.png";
import { getMm } from "../theme/modernMaterialEnterprise";
import useAnmalningLinkLanguage from "../hooks/useAnmalningLinkLanguage";
import { trialFollowupCopyForKind } from "../utils/anmalningTrialFollowupI18n";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const FONT = APP_FONT_FAMILY;

function normalizeTrialFollowupApiPayload(data) {
  if (!data || typeof data !== "object") {
    return { kind: "error", loginUrl: null };
  }
  return {
    kind: data.kind || "error",
    loginUrl: data.loginUrl || null,
  };
}

function AnmalningTrialFollowupContent() {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx } = useSystemButtonStyles();
  const location = useLocation();
  useAnmalningLinkLanguage(location.search);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const answer = searchParams.get("answer");

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);

  useEffect(() => {
    if (!token || (answer !== "yes" && answer !== "no")) {
      setLoading(false);
      setResult({ kind: "invalid", loginUrl: null });
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const base = process.env.REACT_APP_CONNECTION_API || "";
        const url = `${base}/api/public/anmalningar/trial-followup/respond?token=${encodeURIComponent(token)}&answer=${encodeURIComponent(answer)}&format=json`;
        const res = await fetch(url);
        const data = await res.json();
        if (!cancelled) setResult(normalizeTrialFollowupApiPayload(data));
      } catch {
        if (!cancelled) setResult({ kind: "error", loginUrl: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, answer]);

  const copy = useMemo(
    () => trialFollowupCopyForKind(result?.kind, t),
    [result?.kind, t, i18n.language, i18n.resolvedLanguage]
  );

  const severity =
    copy.kind === "accepted" || copy.kind === "already_member"
      ? "success"
      : copy.kind === "declined" || copy.kind === "already"
        ? "info"
        : "error";

  const Icon =
    severity === "success" ? CheckCircleIcon : severity === "info" ? InfoIcon : ErrorOutlineIcon;

  return (
    <Box sx={authPageOuterSx(isDarkMode)}>
      <CssBaseline />
      <AuthPageToggleBar />
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={isDarkMode ? newEraLogo : LOGO_LIGHT}
            alt={t("anmalningar_trial_followup_logo_alt")}
            sx={{ height: 40, mb: 2 }}
          />
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.5rem", color: getMm(isDarkMode).onSurface }}>
            {t("anmalningar_trial_followup_title")}
          </Typography>
        </Box>

        <Box sx={{ ...cardShellSx, p: { xs: 2.5, sm: 4 } }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
              <CircularProgress aria-label={t("loading")} />
              <Typography sx={{ fontFamily: FONT, color: getMm(isDarkMode).onSurfaceVariant, fontSize: "0.875rem" }}>
                {t("anmalningar_trial_followup_loading")}
              </Typography>
            </Box>
          ) : (
            <Box key={`${copy.kind}-${i18n.resolvedLanguage || i18n.language}`}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Icon
                  sx={{
                    fontSize: 48,
                    color: severity === "success" ? "#10B981" : severity === "info" ? "#3B82F6" : "#EF4444",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "1.125rem",
                  textAlign: "center",
                  mb: 1,
                  color: getMm(isDarkMode).onSurface,
                }}
              >
                {copy.heading}
              </Typography>
              <Alert severity={severity} sx={{ mb: 2, borderRadius: "8px" }}>
                <Typography component="span" sx={{ fontFamily: FONT, fontSize: "0.875rem", lineHeight: 1.55 }}>
                  {copy.message}
                </Typography>
              </Alert>
              {result?.loginUrl ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button component={RouterLink} to="/login" variant="contained" sx={primaryButtonSx}>
                    {t("anmalningar_trial_followup_go_login")}
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default function AnmalningTrialFollowupPage() {
  return (
    <CustomThemeProvider>
      <AnmalningTrialFollowupContent />
    </CustomThemeProvider>
  );
}
